/* eslint-disable react-refresh/only-export-components */
import React, {createContext, FC, useCallback, useContext, useEffect, useState} from 'react';
import {apiClient} from '@/shared/services/apiClient';
import {store} from '@/store';
import {queryClient} from '@/shared/contexts/QueryProvider';
import {USER_DATA_KEY} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {logError} from '@/shared/utils/logger';

interface AuthContextProps {
    localStorageTokenKeyName: string;
    user?: UserInfo;
    setUser: (info?: UserInfo) => void;
    setToken: (token: string) => void;
    onLogin: (info: UserInfo) => void;
    onLogout: () => void;
    isInitialized: boolean;
}

interface AuthProviderProps {
    localStorageTokenKeyName?: string;
    authTokenKeyName?: string;
    initialCheckToken?: boolean;
    user?: UserInfo;
    onLogin?: (info: UserInfo) => void;
    onLogout?: () => void;
    children?: React.ReactNode;
}

export interface UserInfo {
    username?: string;
    token?: string;
    isLoggedIn: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userInfo?: any;
    sessionOnly?: boolean;
}

const AuthContext = createContext({} as AuthContextProps);

const AuthProvider: FC<AuthProviderProps> = ({
    authTokenKeyName = 'token',
    localStorageTokenKeyName = 'token',
    user: _user = {isLoggedIn: false},
    onLogin: _onLogin,
    onLogout: _onLogout,
    initialCheckToken = true,
    children,
}) => {
    const [user, setUserState] = useState<UserInfo | undefined>(_user || {isLoggedIn: false});
    const [isInitialized, setIsInitialized] = useState<boolean>(!initialCheckToken);
    const [, setUseSessionStorageForAuth] = useState<boolean>(false);
    const clearLegacyToken = useCallback(() => {
        try {
            localStorage.removeItem(localStorageTokenKeyName);
        } catch {
            // Ignore storage errors
        }
    }, [localStorageTokenKeyName]);

    const removeSessionToken = useCallback(() => {
        try {
            sessionStorage.removeItem(authTokenKeyName);
        } catch {
            // Ignore sessionStorage errors
        }
    }, [authTokenKeyName]);

    const getStoredUser = useCallback((useSession: boolean) => {
        try {
            const stored = useSession ? sessionStorage.getItem('user') : localStorage.getItem('user');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    }, []);

    // Persist user data to storage. For session-only (impersonation) sessions,
    // include the token in sessionStorage so the session survives a page refresh.
    // sessionStorage is tab-scoped and cleared on tab close.
    const storeUser = useCallback((value: UserInfo, useSession: boolean) => {
        try {
            const storage = useSession ? sessionStorage : localStorage;
            if (useSession) {
                // Impersonation: keep the token so a refresh doesn't kill the session
                storage.setItem('user', JSON.stringify(value));
            } else {
                // Normal login: strip token from storage (kept in memory only)
                const {token: _token, ...persisted} = value;
                void _token;
                storage.setItem('user', JSON.stringify(persisted));
            }
        } catch {
            // Ignore storage errors
        }
    }, []);

    const clearSessionAuthState = useCallback(() => {
        // Session-only auth is used for patient launch; clear it when returning to persistent sessions.
        removeSessionToken();
        try {
            sessionStorage.removeItem('user');
        } catch {
            // Ignore sessionStorage errors
        }
    }, [removeSessionToken]);

    const updateSessionMode = useCallback(
        (sessionOnly: boolean) => {
            if (!sessionOnly) {
                clearSessionAuthState();
            }
            setUseSessionStorageForAuth(sessionOnly);
            apiClient.setSessionStorageMode(sessionOnly);
            try {
                if (sessionOnly) {
                    sessionStorage.setItem('auth_session_only', '1');
                } else {
                    sessionStorage.removeItem('auth_session_only');
                }
            } catch {
                // Ignore sessionStorage errors
            }
        },
        [clearSessionAuthState, setUseSessionStorageForAuth]
    );

    const setUser = useCallback(
        (info?: UserInfo) => {
            setUserState((prev) => {
                if (!info) {
                    updateSessionMode(false);
                    return info;
                }
                const sessionOnly = info.sessionOnly ?? prev?.sessionOnly ?? false;
                updateSessionMode(sessionOnly);
                return {...info, sessionOnly};
            });
        },
        [updateSessionMode]
    );

    const onLogin = useCallback(
        // TODO login response object?
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (info: any) => {
            const sessionOnly = Boolean(info?.sessionOnly);
            const newToken = info[authTokenKeyName];
            if (newToken) {
                apiClient.setToken(newToken);
            }

            // Handle both cases: info.userInfo exists OR userInfo is directly in info
            const userData = info?.userInfo || info;

            const newUser = {
                token: newToken,
                userInfo: userData, // Store the actual user data
                username: info?.username || userData?.email,
                isLoggedIn: true,
                sessionOnly,
            };

            setUser(newUser);

            if (_onLogin) _onLogin(info);
        },
        [_onLogin, authTokenKeyName, setUser]
    );

    const onLogout = useCallback(() => {
        setUser({
            isLoggedIn: false,
            sessionOnly: false,
        });

        // Clear cached data: bust the React Query /api/user/data cache (tests
        // now live there, not in Redux) + reset the patient slice.
        try {
            queryClient.removeQueries({queryKey: [USER_DATA_KEY]});
            store.dispatch({type: 'patient/resetPatientState'});
        } catch (error) {
            logError('Failed to reset cached state on logout', error);
        }

        // Clear all localStorage data
        try {
            localStorage.clear();
        } catch {
            // Fallback: try to remove specific items we know about
            try {
                localStorage.removeItem('token');
                localStorage.removeItem('currentPatient');
                localStorage.removeItem('userInfo');
            } catch {
                // Ignore fallback errors
            }
        }

        // Clear client-side cookies (Note: httpOnly cookies can only be cleared by server)
        try {
            const clearCookieForDomain = (cookieName: string, domain?: string) => {
                const domainStr = domain ? `;domain=${domain}` : '';
                const pastDate = new Date(0).toUTCString();

                // Try multiple path variations
                const paths = ['/', '/api', ''];
                paths.forEach((path) => {
                    document.cookie = `${cookieName}=;expires=${pastDate};path=${path}${domainStr}`;
                    if (domain) {
                        document.cookie = `${cookieName}=;expires=${pastDate};path=${path}`;
                    }
                });
            };

            // Get current cookies that JavaScript can access
            const cookies = document.cookie.split(';');
            const domain = window.location.hostname;
            const domainParts = domain.split('.');

            // Domains to try clearing cookies for
            const domainsToTry = [
                undefined, // Current domain without explicit domain attribute
                domain,
                '.' + domain,
            ];

            // If we have subdomains, also try parent domain
            if (domainParts.length > 2) {
                const parentDomain = '.' + domainParts.slice(-2).join('.');
                domainsToTry.push(parentDomain);
            }

            // Clear each cookie for each domain variation
            cookies.forEach((cookie) => {
                const cookieName = cookie.split('=')[0].trim();
                if (cookieName) {
                    domainsToTry.forEach((domainToTry) => {
                        clearCookieForDomain(cookieName, domainToTry);
                    });
                }
            });

            // Also try to clear common cookie names that might be httpOnly
            const commonCookieNames = ['refreshToken', 'refresh_token', 'refresh', 'session', 'auth', 'token'];
            commonCookieNames.forEach((cookieName) => {
                domainsToTry.forEach((domainToTry) => {
                    clearCookieForDomain(cookieName, domainToTry);
                });
            });
        } catch {
            // Ignore cookie clearing errors
        }

        // Clear session storage as well for completeness
        try {
            sessionStorage.clear();
        } catch {
            // Ignore cookie clearing errors
        }

        clearLegacyToken();
        if (_onLogout) _onLogout();
    }, [_onLogout, clearLegacyToken, setUser]);

    const setToken = useCallback(
        (token?: string) => {
            if (token === undefined) {
                setUserState((old) => ({
                    ...(old || {}),
                    isLoggedIn: false,
                    token: undefined,
                }));
                removeSessionToken();
                clearLegacyToken();
                apiClient.clearToken();
            } else {
                setUserState((old) => ({
                    ...(old || {}),
                    isLoggedIn: old?.isLoggedIn ?? false,
                    token,
                }));
                apiClient.setToken(token);
            }
        },
        [clearLegacyToken, removeSessionToken]
    );

    useEffect(() => {
        if (initialCheckToken) {
            const sessionOnlyFromStorage = sessionStorage.getItem('auth_session_only') === '1';
            const isLaunchRoute = typeof window !== 'undefined' && window.location.pathname === '/launch';
            const hasLaunchTokenParam =
                typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('token');
            const shouldUseSessionOnly = sessionOnlyFromStorage || (isLaunchRoute && hasLaunchTokenParam);

            if (shouldUseSessionOnly && !sessionOnlyFromStorage) {
                try {
                    sessionStorage.setItem('auth_session_only', '1');
                } catch {
                    // Ignore storage errors
                }
            }

            updateSessionMode(shouldUseSessionOnly);
            const storedUser = getStoredUser(shouldUseSessionOnly);

            if (shouldUseSessionOnly) {
                // Restore impersonation session from sessionStorage (survives page refresh).
                if (storedUser?.token) {
                    apiClient.setToken(storedUser.token);
                    setUserState({
                        token: storedUser.token,
                        userInfo: storedUser.userInfo,
                        username: storedUser.username,
                        isLoggedIn: true,
                        sessionOnly: true,
                    });
                }
                setIsInitialized(true);
                return;
            }

            apiClient
                .manualRefresh()
                .then((token) => {
                    if (token) {
                        setUserState({
                            token,
                            userInfo: storedUser?.userInfo,
                            username: storedUser?.username,
                            isLoggedIn: true,
                            sessionOnly: shouldUseSessionOnly,
                        });
                    }
                })
                .finally(() => {
                    setIsInitialized(true);
                });
        }
    }, [getStoredUser, initialCheckToken, updateSessionMode]);

    // Sync API client token whenever user token changes
    useEffect(() => {
        if (user?.token) {
            apiClient.setToken(user.token);
        } else {
            apiClient.clearToken();
        }
    }, [user?.token]);

    // Save user data to localStorage for impersonation recovery (simple sync)
    useEffect(() => {
        if (user?.isLoggedIn && user?.userInfo) {
            storeUser(user, Boolean(user?.sessionOnly));
        }
    }, [storeUser, user]);

    // Set up callback for API client token updates (for refresh scenarios)
    useEffect(() => {
        const handleTokenUpdate = (newToken: string) => {
            if (newToken) {
                setUserState((prevUser) => ({
                    ...(prevUser || {}),
                    token: newToken,
                    isLoggedIn: true,
                }));
            } else {
                // Token was cleared
                removeSessionToken();
                clearLegacyToken();
                setUserState({
                    isLoggedIn: false,
                });
            }
        };

        apiClient.setTokenUpdatedCallback(handleTokenUpdate);

        // Cleanup
        return () => {
            apiClient.setTokenUpdatedCallback(() => {});
        };
    }, [clearLegacyToken, removeSessionToken]);

    return (
        <AuthContext.Provider
            value={{
                localStorageTokenKeyName,
                user,
                setUser,
                setToken,
                onLogin,
                onLogout,
                isInitialized,
            }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuthContext must be used within an AuthContext.Provider');
    }
    return context;
};

export default AuthProvider;
